import type { Module } from 'vuex'
import type { Dictionary } from 'vue-router/types/router'
import type {
  MessageState,
  SimpleMessageFormChanges,
  EmailMessageFormChanges,
  CsvPreviewContact,
  ConversionTableData,
  ABStats,
  ConversionGraphTimeseries,
  MessageLink, RecipientFilter,
} from './types'
import { messageActions } from './actions'
import {
  getMessageUiStatus,
  getAvailableMessageOptionsMap,
  getInitialSmsPreview,
  getInitialSimpleMessageForm,
  getInitialEmailMessageForm,
  countSmsPreviewSegments,
  calculateScheduleMessageCountdown,
  generateScheduledMessageCountdownCopy,
  generateScheduledMessageCountdownTime,
  getDynamicTagsFromContent,
  getMessageCurrency,
  getNoneExistingDynamicTags,
  generateNonExistingDynamicTagsErrorMessage,
  generatePreviewContacts,
  countSmsBodyLength,
  keepKeysInObject,
  hasEmptyKeysInObject,
} from './utils'
import type { RootState } from '@/store/modules/types'
import { clone, mergeObjects, convertToSanitizedCamelCase } from '@/utils/helpers'
import type { EmailSenderProperty } from '@/api/message-senders/types'
import {
  injectInformationToUnlayerHtml,
  injectInformationToRichTextHtml,
  injectInformationToBeefreeHtml,
  getPreviewAndFooterHtml,
} from '@/utils/email'
import { mergeDynamicTags } from '@/utils/message'
import { convertJsonToCsvFile } from '@/utils/csv'

export const defaultTimeWindowOptions = [1, 7, 14]
const defaultTimeWindow = 14

export const initialMessageState = (): MessageState => ({
  // Additional Dynamic Tags
  additionalDynamicTags: [],
  defaultEmailDynamicTags: ['email_address', 'first_name'],
  defaultSmsDynamicTags: ['mobile_number'],
  // Message Form
  isSavingMessageAsDraft: false,
  isSendingOutMessage: false,
  isSendingTestEmail: false,
  // Simple Message Form
  smsMessagePreview: getInitialSmsPreview(),
  simpleMessageForm: getInitialSimpleMessageForm(),
  fallbackDynamicTagsForSimpleMessageForm: [],
  // Email Message Form
  emailMessageForm: getInitialEmailMessageForm(),
  fallbackDynamicTagsForEmailMessageForm: [],
  emailMessageFormUnsaved: false,
  emailMessageFormVariants: {},
  // Scratch Email Html
  savedEmail: null,
  scratchEmail: null,
  isUpdatingEmail: false,
  isFetchingSavedEmail: false,
  // Messages
  messages: [],
  totalMessagesCount: 0,
  isFetchingMessages: false,
  isNoMoreMessages: false,
  hasFetchMessagesFailed: false,
  // Overview messages
  messagesOverview: {},
  isFetchingOverviewMessages: false,
  hasFetchOverviewMessagesFailed: false,
  overviewLinkedEvent: null,
  // Current Message
  currentSelectedMessage: null,
  isFetchingMessage: false,
  // Csv Preview Contacts
  csvPreviewContacts: null,
  selectedCsvPreviewContactIndex: null,
  displayCsvPreviewContacts: false,
  isFetchingSmsCost: false,
  smsCostFailedToFetch: false,
  // Message states timeseries
  isFetchingMessageActionStatsTimeseries: false,
  messageActionStatsTimeseries: [],
  // Message delivery stats
  isFetchingMessageDeliveryStats: false,
  messageDeliveryStats: null,
  // Conversions
  conversionSelectedEvents: [],
  conversionTableData: [],
  conversionStatsByTimeWindows: defaultTimeWindowOptions.reduce((acc, option) => ({ ...acc, [option]: [] }), {}),
  conversionGraphTimeseriesByTimeWindows: defaultTimeWindowOptions.reduce((acc, option) => ({ ...acc, [option]: [] }), {}),
  isFetchingConversionTableData: false,
  isFetchingConversionGraph: false,
  isFetchingConversionStats: false,
  conversionTableSearch: '',
  conversionTableCount: 0,
  isCalculationsFinished: true,
  calculationsTimeoutId: null,
  conversionTimeWindow: defaultTimeWindow,
  conversionSelectedLinks: [],
  nextUpdateStartDate: null,
  // AB
  isFetchingABMessage: false,
  statsAB: null,
  selectedVariant: 'A',
  // HTML email
  isUploadingHtmlEmail: false,
  htmlEmailUploadFailed: false,
  htmlEmail: null,
  // Restricted domain
  emailDomainRestricted: false,
  domainRestricted: false,
  // Message links
  messageLinks: [],
  isFetchingMessageLinks: false,
})

const messageModule: Module<MessageState, RootState> = {
  namespaced: true,
  state: initialMessageState,
  actions: messageActions,
  mutations: {
    // Default Dynamic Tags
    RESET_ADDITIONAL_DYNAMIC_TAGS(state) {
      state.additionalDynamicTags = initialMessageState().additionalDynamicTags
    },
    SET_ADDITIONAL_DYNAMIC_TAGS(state, dynamicTags: string[]) {
      state.additionalDynamicTags = clone(dynamicTags)
    },
    // Message Form
    SET_IS_SAVING_MESSAGE_AS_DRAFT(state, isSaving: boolean) {
      state.isSavingMessageAsDraft = isSaving
    },
    SET_IS_SENDING_OUT_MESSAGE(state, isSending: boolean) {
      state.isSendingOutMessage = isSending
    },
    SET_IS_SENDING_TEST_EMAIL(state, isSending: boolean) {
      state.isSendingTestEmail = isSending
    },
    // Email Message Form
    RESET_EMAIL_MESSAGE_FORM(state) {
      state.emailMessageForm = getInitialEmailMessageForm()
    },
    SET_EMAIL_MESSAGE_FORM(state, emailMessageForm: EmailMessageForm) {
      const newEmailMessageForm: EmailMessageForm = clone(emailMessageForm)
      // For backward-compatible, if templateType is not given, default to 'beefree'
      if (!newEmailMessageForm.meta.presentation.templateType) {
        newEmailMessageForm.meta.presentation.templateType = 'beefree'
      }
      state.emailMessageForm = newEmailMessageForm
    },
    PATCH_EMAIL_MESSAGE_FORM(state, changes: EmailMessageFormChanges) {
      state.emailMessageForm = mergeObjects(state.emailMessageForm, changes)
    },
    PATCH_AB_EMAIL_MESSAGE(state, meta) {
      const variantA = clone(state.emailMessageFormVariants['A'])
      variantA.meta.messageBody.subject = meta.subjectA
      variantA.meta.messageBody.previewText = meta.previewA
      const variantB = clone(state.emailMessageFormVariants['B'])
      variantB.meta.messageBody.subject = meta.subjectB
      variantB.meta.messageBody.previewText = meta.previewB
      state.emailMessageFormVariants = { A: variantA, B: variantB }
    },
    CREATE_AB_EMAIL_MESSAGE(state, data: any) {
      if (data && data.promoterMessages && data.promoterMessages.length > 1) {
        const variantA: EmailMessageForm = clone(data.message)
        variantA.meta = data.promoterMessages[0].meta
        variantA.promoterMessageOid = data.promoterMessages[0].oid
        const variantB: EmailMessageForm = clone(data.message)
        variantB.meta = data.promoterMessages[1].meta
        variantB.promoterMessageOid = data.promoterMessages[1].oid
        state.emailMessageFormVariants = { A: variantA, B: variantB }
      }
      else {
        const originalMessage: EmailMessageForm = clone(state.emailMessageForm)
        state.emailMessageFormVariants = { A: originalMessage, B: originalMessage }
      }
    },
    UPDATE_AB_EMAIL_STATE(state, type) {
      state.emailMessageFormVariants[type] = clone(state.emailMessageForm)
    },
    REMOVE_AB_EMAIL_MESSAGE(state) {
      state.emailMessageForm = clone(state.emailMessageFormVariants['A'])
    },
    SELECT_AB_EMAIL_MESSAGE(state, variantKey) {
      console.log('SELECT_AB_EMAIL_MESSAGE', variantKey)
      const variantMessage: EmailMessageForm = clone(state.emailMessageFormVariants[variantKey])
      const originalMessage: EmailMessageForm = clone(state.emailMessageForm)
      originalMessage.meta.presentation = variantMessage.meta.presentation
      originalMessage.meta.messageBody = variantMessage.meta.messageBody
      state.emailMessageForm = originalMessage
      state.selectedVariant = variantKey
    },
    PATCH_AB_EMAIL_MESSAGE_TEMPLATE(state, template) {
      const variantMessage: EmailMessageForm = clone(state.emailMessageFormVariants[state.selectedVariant])
      variantMessage.meta.presentation.template = template
      state.emailMessageFormVariants[state.selectedVariant] = variantMessage
    },
    PATCH_AB_EMAIL_MESSAGE_TEMPLATE_TYPE(state, templateType) {
      const variantMessage: EmailMessageForm = clone(state.emailMessageFormVariants[state.selectedVariant])
      variantMessage.meta.presentation.templateType = templateType
      state.emailMessageFormVariants[state.selectedVariant] = variantMessage
    },
    PATCH_AB_EMAIL_MESSAGE_VARIANT_RESOURCE_ID(state, templateResourceOid) {
      state.emailMessageFormVariants[state.selectedVariant].meta.templateResourceOid = templateResourceOid
    },
    SWITCH_AB_EMAIL_STATE(state, type) {
      console.log('SWITCH_AB_EMAIL_STATE')
      if (type.type === 'A') {
        state.emailMessageFormVariants['B'] = type.data
      }
      else {
        state.emailMessageFormVariants['A'] = type.data
      }
      state.selectedVariant = type.type
      const variantMessage: EmailMessageForm = clone(state.emailMessageFormVariants[type.type])
      const originalMessage: EmailMessageForm = clone(state.emailMessageForm)
      originalMessage.meta.presentation = variantMessage.meta.presentation
      originalMessage.meta.messageBody = variantMessage.meta.messageBody
      state.emailMessageForm = originalMessage
    },
    SET_EMAIL_MESSAGE_FORM_UNSAVED(state, unsaved: boolean) {
      state.emailMessageFormUnsaved = unsaved
    },
    // Scratch Email Html
    RESET_SCRATCH_EMAIL_TEMPLATE(state) {
      state.scratchEmail = initialMessageState().scratchEmail
    },
    SET_SCRATCH_EMAIL_TEMPLATE(state, html: string) {
      state.scratchEmail = html
    },
    SET_IS_UPDATING_EMAIL(state, isUploading: boolean) {
      state.isUpdatingEmail = isUploading
    },
    SET_IS_FETCHING_SAVED_EMAIL(state, isFetching: boolean) {
      state.isFetchingSavedEmail = isFetching
    },
    SET_SAVED_EMAIL(state, template: string) {
      state.savedEmail = template
    },
    // Simple Message Form
    RESET_SMS_MESSAGE_PREVIEW(state) {
      state.smsMessagePreview = getInitialSmsPreview()
    },
    PUT_SMS_MESSAGE_PREVIEW(state, smsMessagePreview: SMSMessagePreview) {
      state.smsMessagePreview = mergeObjects(state.smsMessagePreview, smsMessagePreview)
    },
    RESET_SIMPLE_MESSAGE_FORM(state) {
      state.simpleMessageForm = getInitialSimpleMessageForm()
    },
    SET_SIMPLE_MESSAGE_FORM(state, simpleMessageForm: SimpleMessageForm) {
      const newSimpleMessageForm: SimpleMessageForm = clone(simpleMessageForm)
      // Only apply strict validation for new messages, not drafts
      // If loading an existing message (with an OID), preserve scheduling data
      if (!simpleMessageForm.oid && (!newSimpleMessageForm.meta.presentation.timeZone || !newSimpleMessageForm.scheduledAt)) {
        newSimpleMessageForm.meta.presentation.timeZone = null
        newSimpleMessageForm.scheduledAt = null
      }
      state.simpleMessageForm = clone(newSimpleMessageForm)
    },
    SET_FALLBACK_DYNAMIC_TAGS_FOR_EMAIL_MESSAGE_FORM(state, fallbackDynamicTags: string[]) {
      state.fallbackDynamicTagsForEmailMessageForm = fallbackDynamicTags
    },
    SET_FALLBACK_DYNAMIC_TAGS_FOR_SIMPLE_MESSAGE_FORM(state, fallbackDynamicTags: string[]) {
      state.fallbackDynamicTagsForSimpleMessageForm = fallbackDynamicTags
    },
    TIDY_UP_FALLBACK_DYNAMIC_TAGS_IN_EMAIL_MESSAGE(state, fallbackDynamicTags: string[]) {
      const newEmailMessageForm: EmailMessageForm = clone(state.emailMessageForm)
      const fallbackDynamicTagsInCamelCase = fallbackDynamicTags.map(k => convertToSanitizedCamelCase(k))
      newEmailMessageForm.meta.fallbackDynamicTags
        = keepKeysInObject(
          newEmailMessageForm.meta.fallbackDynamicTags || {},
          fallbackDynamicTagsInCamelCase)
      state.emailMessageForm = newEmailMessageForm
    },
    TIDY_UP_FALLBACK_DYNAMIC_TAGS_IN_SIMPLE_MESSAGE(state, fallbackDynamicTags: string[]) {
      const newSimpleMessageForm: SimpleMessageForm = clone(state.simpleMessageForm)
      const fallbackDynamicTagsInCamelCase = fallbackDynamicTags.map(k => convertToSanitizedCamelCase(k))
      newSimpleMessageForm.meta.fallbackDynamicTags
        = keepKeysInObject(
          newSimpleMessageForm.meta.fallbackDynamicTags || {},
          fallbackDynamicTagsInCamelCase)
      state.simpleMessageForm = newSimpleMessageForm
    },
    REMOVE_DYNAMIC_TAGS_IN_EMAIL_MESSAGE_FORM(state) {
      const newEmailMessageForm: EmailMessageForm = clone(state.emailMessageForm)

      newEmailMessageForm.meta.dynamicTagHeaders = []
      newEmailMessageForm.meta.tagsResourceOid = null

      state.emailMessageForm = newEmailMessageForm
    },
    REMOVE_DYNAMIC_TAGS_IN_SIMPLE_MESSAGE_FORM(state) {
      const newSimpleMessageForm: SimpleMessageForm = clone(state.simpleMessageForm)

      newSimpleMessageForm.meta.dynamicTagHeaders = []
      newSimpleMessageForm.meta.tagMaxWidthMap = {}
      newSimpleMessageForm.meta.tagsResourceOid = null

      state.simpleMessageForm = newSimpleMessageForm
    },
    PUT_FILTERING_IN_EMAIL_MESSAGE_FORM(state, targetingValues: RecipientFilter | null) {
      const newEmailMessageForm: EmailMessageForm = clone(state.emailMessageForm)
      newEmailMessageForm.meta.recipientFilter = clone(targetingValues)
      state.emailMessageForm = newEmailMessageForm
    },
    PUT_FILTERING_IN_SIMPLE_MESSAGE_FORM(state, targetingValues: RecipientFilter | null) {
      const newSimpleMessageForm: SimpleMessageForm = clone(state.simpleMessageForm)
      newSimpleMessageForm.meta.recipientFilter = clone(targetingValues)
      state.simpleMessageForm = newSimpleMessageForm
    },
    PUT_SIMPLE_MESSAGE_FORM(state, simpleMessageFormChanges: SimpleMessageFormChanges) {
      const newSimpleMessageForm = mergeObjects(state.simpleMessageForm, simpleMessageFormChanges)
      // Override tagMaxWidthMap, instead of merging
      if (simpleMessageFormChanges.meta && simpleMessageFormChanges.meta.tagMaxWidthMap) {
        newSimpleMessageForm.meta.tagMaxWidthMap = simpleMessageFormChanges.meta.tagMaxWidthMap
      }
      state.simpleMessageForm = newSimpleMessageForm
    },
    // Messages
    RESET_MESSAGES(state) {
      state.isNoMoreMessages = false
      state.hasFetchMessagesFailed = false
      state.messages = []
      state.totalMessagesCount = 0
    },
    SET_TOTAL_MESSAGES_COUNT(state, count: number) {
      // For event groups, use backend count; for single events, fallback to array length
      state.totalMessagesCount = count > 0 ? count : state.messages.length
    },
    RESET_OVERVIEW_MESSAGES(state) {
      state.hasFetchOverviewMessagesFailed = false
      state.messagesOverview = {}
    },
    RESET_OVERVIEW_LINKED_EVENT(state) {
      state.overviewLinkedEvent = null
    },
    SET_OVERVIEW_LINKED_EVENT(state, eventOid: number) {
      state.overviewLinkedEvent = eventOid
    },
    CONCAT_MESSAGES(state, messages) {
      state.messages = clone(state.messages.concat(messages))
    },
    SET_OVERVIEW_MESSAGES(state, messages) {
      state.messagesOverview = clone(messages)
    },
    REMOVE_MESSAGE_FROM_MESSAGES(state, messageOid: number) {
      state.messages = clone(state.messages.filter(msg => msg.oid !== messageOid))
    },
    SET_IS_FETCHING_MESSAGES(state, isFetching) {
      state.isFetchingMessages = isFetching
    },
    SET_IS_FETCHING_OVERVIEW_MESSAGES(state, isFetching) {
      state.isFetchingOverviewMessages = isFetching
    },
    SET_HAS_FETCH_MESSAGES_FAILED(state, hasFailed) {
      state.hasFetchMessagesFailed = hasFailed
    },
    SET_HAS_FETCH_OVERVIEW_MESSAGES_FAILED(state, hasFailed) {
      state.hasFetchOverviewMessagesFailed = hasFailed
    },
    SET_IS_NO_MORE_MESSAGES(state, isNoMoreMessages) {
      state.isNoMoreMessages = isNoMoreMessages
    },
    // Current message
    SET_CURRENT_SELECTED_MESSAGE(state, message: SimpleMessage | EmailMessage) {
      state.currentSelectedMessage = clone(message)
    },
    RESET_CURRENT_SELECTED_MESSAGE(state) {
      state.currentSelectedMessage = null
    },
    SET_IS_FETCHING_MESSAGE(state, isFetchingMessage) {
      state.isFetchingMessage = isFetchingMessage
    },
    // Csv Preview Contacts
    RESET_CSV_PREVIEW_CONTACTS(state) {
      const { csvPreviewContacts, selectedCsvPreviewContactIndex, displayCsvPreviewContacts } = initialMessageState()
      state.csvPreviewContacts = csvPreviewContacts
      state.selectedCsvPreviewContactIndex = selectedCsvPreviewContactIndex
      state.displayCsvPreviewContacts = displayCsvPreviewContacts
    },
    SET_CSV_PREVIEW_CONTACTS(state, { headers, rows }) {
      state.csvPreviewContacts = generatePreviewContacts(headers, rows)
      state.selectedCsvPreviewContactIndex = 0
    },
    SET_SELECTED_CSV_PREVIEW_CONTACTS_INDEX(state, index: number) {
      state.selectedCsvPreviewContactIndex = index
    },
    TOGGLE_DISPLAY_CSV_PREVIEW_CONTACTS(state) {
      state.displayCsvPreviewContacts = !state.displayCsvPreviewContacts
    },
    SET_IS_FETCHING_SMS_COST(state, isFetching: boolean) {
      state.isFetchingSmsCost = isFetching
    },
    SET_SMS_COST_FAILED_TO_FETCH(state, hasFailed: boolean) {
      state.smsCostFailedToFetch = hasFailed
    },
    SET_MESSAGE_ACTION_STATS_TIMESERIES(state, timeseries) {
      // Create a deep clone to avoid reference issues
      const ts = JSON.parse(JSON.stringify(timeseries))

      // Create new arrays instead of mutating
      Object.keys(ts).forEach((key) => {
        if (key === 'daily' || key === 'weekly') {
          ts[key] = [...ts[key]].reverse()
        }
      })

      // Assign to state
      state.messageActionStatsTimeseries = ts
    },
    SET_IS_FETCHING_MESSAGE_ACTION_STATS_TIMESERIES(state, isFetching: boolean) {
      state.isFetchingMessageActionStatsTimeseries = isFetching
    },
    SET_MESSAGE_DELIVERY_STATS(state, stats) {
      state.messageDeliveryStats = stats
    },
    SET_IS_FETCHING_MESSAGE_DELIVERY_STATS(state, isFetching: boolean) {
      state.isFetchingMessageDeliveryStats = isFetching
    },

    // Conversions
    SET_CONVERSIONS_SELECTED_EVENTS(state, selectedEvents: string[]) {
      state.conversionSelectedEvents = selectedEvents.map(oid => Number(oid))
    },

    SET_CONVERSIONS_TABLE_DATA(state, tableData: ConversionTableData[]) {
      state.conversionTableData = tableData
    },

    SET_IS_FETCHING_CONVERSIONS_STATS(state, isFetching: boolean) {
      state.isFetchingConversionStats = isFetching
    },

    SET_IS_FETCHING_CONVERSIONS_TABLE_DATA(state, isFetching: boolean) {
      state.isFetchingConversionTableData = isFetching
    },

    SET_IS_FETCHING_CONVERSIONS_GRAPH(state, isFetching: boolean) {
      state.isFetchingConversionGraph = isFetching
    },

    SET_CONVERSION_GRAPH_TIMESERIES_BY_TIME_WINDOWS(state, graphData: Dictionary<ConversionGraphTimeseries[]>) {
      state.conversionGraphTimeseriesByTimeWindows = graphData
    },

    SET_CONVERSIONS_STATS_BY_TIME_WINDOWS(state, stats: any) {
      state.conversionStatsByTimeWindows = stats
    },

    SET_CONVERSION_TABLE_SEARCH(state, search: string) {
      state.conversionTableSearch = search
    },

    SET_CONVERSION_TABLE_COUNT(state, count: number) {
      state.conversionTableCount = count
    },

    SET_IS_CALCULATIONS_FINISHED(state, isFinished: boolean) {
      state.isCalculationsFinished = isFinished
    },
    SET_CALCULATIONS_TIMEOUT_ID(state, value: number | null) {
      state.calculationsTimeoutId = value
    },

    SET_CONVERSIONS_TIME_WINDOW(state, value: number) {
      state.conversionTimeWindow = value
    },

    SET_CONVERSIONS_LINK(state, value: string[]) {
      state.conversionSelectedLinks = value.map(oid => Number(oid))
    },

    CLEAR_CONVERSIONS_TIME_WINDOW(state) {
      state.conversionTimeWindow = defaultTimeWindow
    },

    SET_CONVERSIONS_NEXT_UPDATE_START_DATE(state, value: string | null) {
      state.nextUpdateStartDate = value
    },

    // AB
    SET_IS_FETCHING_AB_MESSAGE(state, isFetching) {
      state.isFetchingABMessage = isFetching
    },

    SET_STATS_AB(state, stats: ABStats[] | null) {
      state.statsAB = stats
    },

    RESET_SWITCH_AB(state) {
      state.selectedVariant = 'A'
    },

    SET_MESSAGE_LINKS(state, links: MessageLink[]) {
      state.messageLinks = links
    },

    SET_IS_FETCHING_MESSAGE_LINKS(state, value: boolean) {
      state.isFetchingMessageLinks = value
    },

    SET_IS_UPLOADING_HTML_EMAIL(state, isUploading) {
      state.isUploadingHtmlEmail = isUploading
    },

    SET_HTML_EMAIL(state, oid) {
      state.htmlEmail = oid
    },

    SET_HTML_EMAIL_UPLOAD_FAILED(state, failed) {
      state.htmlEmailUploadFailed = failed
    },

    SET_EMAIL_DOMAIN_RESTRICTED(state, restricted) {
      state.emailDomainRestricted = restricted
    },

    SET_DOMAIN_RESTRICTED(state, restricted) {
      state.domainRestricted = restricted
    },
  },
  getters: {
    hasAdditionalDynamicTags(state) {
      return state.additionalDynamicTags.length !== 0
    },
    availableDynamicTagsAll(_, getters) {
      const allTags = getters.availableDynamicTagsInEmailMessage
      return allTags.filter((item: any, pos: any) => allTags.indexOf(item) === pos)
    },
    availableDynamicTagsInSimpleMessage(state) {
      const availableDynamicTags = mergeDynamicTags(state.defaultSmsDynamicTags, state.simpleMessageForm.meta.dynamicTagHeaders)
      return availableDynamicTags
    },
    availableDynamicTagsInEmailMessage(state) {
      const availableDynamicTags = mergeDynamicTags(state.defaultEmailDynamicTags, state.additionalDynamicTags, state.emailMessageForm.meta.dynamicTagHeaders)
      return availableDynamicTags
    },
    isFallbackDynamicTagsCompleteInSimpleMessage(state) {
      return !hasEmptyKeysInObject(state.simpleMessageForm.meta.fallbackDynamicTags || {})
    },
    isFallbackDynamicTagsCompleteInEmailMessage(state) {
      return !hasEmptyKeysInObject(state.emailMessageForm.meta.fallbackDynamicTags || {})
    },
    hasNonExistingTagsInSimpleMessage(state) {
      const availableDynamicTags = mergeDynamicTags(state.defaultSmsDynamicTags, state.simpleMessageForm.meta.dynamicTagHeaders)
      const nonExistingDynamicTags = getNoneExistingDynamicTags(
        state.simpleMessageForm.meta.messageBody,
        availableDynamicTags,
      )
      return nonExistingDynamicTags.length > 0
    },
    hasNonExistingTagsInEmailMessage(state) {
      const availableDynamicTags = mergeDynamicTags(state.defaultEmailDynamicTags, state.additionalDynamicTags, state.emailMessageForm.meta.dynamicTagHeaders)
      const nonExistingDynamicTags = getNoneExistingDynamicTags(
        state.scratchEmail || '',
        availableDynamicTags,
      )
      return nonExistingDynamicTags.length > 0
    },
    nonExistingTagsInSimpleMessageErrorCopy(state): string | null {
      const availableDynamicTags = mergeDynamicTags(state.defaultSmsDynamicTags, state.simpleMessageForm.meta.dynamicTagHeaders)
      const nonExistingDynamicTags = getNoneExistingDynamicTags(
        state.simpleMessageForm.meta.messageBody,
        availableDynamicTags,
      )
      return generateNonExistingDynamicTagsErrorMessage(nonExistingDynamicTags, 'message')
    },
    nonExistingDynamicTagsInEmailMessageCopy(state): string | null {
      const availableDynamicTags = mergeDynamicTags(state.defaultEmailDynamicTags, state.additionalDynamicTags, state.emailMessageForm.meta.dynamicTagHeaders)
      const nonExistingDynamicTags = getNoneExistingDynamicTags(
        state.scratchEmail || '',
        availableDynamicTags,
      )
      return generateNonExistingDynamicTagsErrorMessage(nonExistingDynamicTags, 'email')
    },
    getBeefreePreviewAndFooterHtmlForInjection(state) {
      return (messageSender: EmailSenderProperty | null) => {
        if (!state.emailMessageForm) { return }
        const hideDefaultEmailFooter = !!state.emailMessageForm.meta.presentation.hideDefaultEmailFooter
        const previewText = state.emailMessageForm.meta.messageBody.previewText
        const senderName = messageSender?.additionalInfo.senderName || null
        const businessAddress = messageSender?.additionalInfo.businessAddress || null
        // Ensure that the above strings are sanitized before inserting them (sanitization currently
        // occurs within below functions)

        return getPreviewAndFooterHtml(
          previewText,
          hideDefaultEmailFooter ? null : senderName,
          hideDefaultEmailFooter ? null : businessAddress,
          !hideDefaultEmailFooter,
        )
      }
    },
    injectInfoToScratchEmail(state) {
      return (messageSender: EmailSenderProperty | null) => {
        if (!state.scratchEmail || !state.emailMessageForm) { return }
        const hideDefaultEmailFooter = !!state.emailMessageForm.meta.presentation.hideDefaultEmailFooter
        const previewText = state.emailMessageForm.meta.messageBody.previewText
        const senderName = messageSender?.additionalInfo.senderName || null
        const businessAddress = messageSender?.additionalInfo.businessAddress || null
        // Ensure that the above strings are sanitized before inserting them (sanitization currently
        // occurs within below functions)
        switch (state.emailMessageForm.meta.presentation.templateType) {
          case 'unlayer':
            return injectInformationToUnlayerHtml(
              state.scratchEmail,
              previewText,
              hideDefaultEmailFooter ? null : senderName,
              hideDefaultEmailFooter ? null : businessAddress,
              !hideDefaultEmailFooter,
            )
          case 'beefree':
            return injectInformationToBeefreeHtml(
              previewText,
              hideDefaultEmailFooter ? null : senderName,
              hideDefaultEmailFooter ? null : businessAddress,
              !hideDefaultEmailFooter,
            )
          case 'rich-text':
            return injectInformationToRichTextHtml(
              state.scratchEmail,
              previewText,
              hideDefaultEmailFooter ? null : senderName,
              hideDefaultEmailFooter ? null : businessAddress,
              !hideDefaultEmailFooter,
            )
          default:
            return null
        }
      }
    },
    emailMessageFormCannotBeTested(state): boolean {
      if (!state.emailMessageForm) {
        return false
      }
      if (state.emailMessageForm.abEmail && state.emailMessageForm.abEmailType === 'subject'
        && state.emailMessageFormVariants['A'] && state.emailMessageFormVariants['B']) {
        return !state.emailMessageFormVariants['A'].meta.messageBody.subject
          || !state.emailMessageFormVariants['B'].meta.messageBody.subject
      }
      return !state.emailMessageForm.meta.messageBody.subject
        || !state.emailMessageForm.meta.email
        || !state.emailMessageForm.meta.email.promoterPropertyOid
    },
    emailMessageFormCampaignOid(state): number | null {
      return state.emailMessageForm.meta.initiator?.campaignOid || null
    },
    emailMessageFormEventOid(state): number | null {
      return state.emailMessageForm.meta.initiator?.eventOid || null
    },
    emailMessageFormSendFromEventOid(state): number | null {
      return state.emailMessageForm.meta.sendFromEvent?.eventOid || null
    },
    emailMessageFormSuggestedAudienceEventOid(state): number | null {
      return state.emailMessageForm.meta.suggestedAudience?.eventOid || null
    },
    simpleMessageFormCampaignOid(state): number | null {
      return state.simpleMessageForm.meta.initiator?.campaignOid || null
    },
    simpleMessageFormEventOid(state): number | null {
      return state.simpleMessageForm.meta.initiator?.eventOid || null
    },
    simpleMessageFormSendFromEventOid(state): number | null {
      return state.simpleMessageForm.meta.sendFromEvent?.eventOid || null
    },
    simpleMessageFormSuggestedAudienceEventOid(state): number | null {
      return state.simpleMessageForm.meta.suggestedAudience?.eventOid || null
    },
    getSmsMessagePreviewSegmentsCount(state) {
      return (simpleMessageForm: SimpleMessageForm) =>
        countSmsPreviewSegments(
          simpleMessageForm.meta.tagMaxWidthMap,
          simpleMessageForm.meta.messageBody + state.smsMessagePreview.optOutMessage,
        )
    },
    smsMessagePreviewSegmentsCount(state) {
      return countSmsPreviewSegments(
        state.simpleMessageForm.meta.tagMaxWidthMap,
        state.smsMessagePreview.body,
      )
    },
    smsMessagePreviewLength(state) {
      return countSmsBodyLength(
        state.simpleMessageForm.meta.tagMaxWidthMap,
        state.smsMessagePreview.body,
      )
    },
    getMessageUiStatus() {
      return (message: SimpleMessage | EmailMessage) => getMessageUiStatus(message)
    },
    getAvailableMessageOptionsMap() {
      return (message: SimpleMessage | EmailMessage) => getAvailableMessageOptionsMap(message)
    },
    currentSelectedMessageCurrency(state) {
      if (!state.currentSelectedMessage) return null
      return getMessageCurrency(state.currentSelectedMessage)
    },
    currentSelectedMessageUiStatus(state) {
      if (!state.currentSelectedMessage) return null
      return getMessageUiStatus(state.currentSelectedMessage)
    },
    currentSelectedMessageEditable(state, getters, rootState): boolean {
      if (!state.currentSelectedMessage) return false
      const uiStatus = getMessageUiStatus(state.currentSelectedMessage)
      if (uiStatus === 'draft') return true
      if (uiStatus === 'scheduled') {
        // Available until 5 minutes before scheduled time
        const countdown = calculateScheduleMessageCountdown(state.currentSelectedMessage, rootState.applicationModule.applicationTime)
        return countdown > 300
      }
      return false
    },
    currentSelectedMessageScheduleCountdownCopy(state, getters, rootState): string | null {
      if (!state.currentSelectedMessage) return null
      const uiStatus = getMessageUiStatus(state.currentSelectedMessage)
      if (uiStatus !== 'scheduled') return null
      const countdown = calculateScheduleMessageCountdown(state.currentSelectedMessage, rootState.applicationModule.applicationTime)
      return generateScheduledMessageCountdownCopy(countdown)
    },
    currentScheduledMessageCountdownTime(state, getters, rootState): string | null {
      if (!state.currentSelectedMessage) return null
      const uiStatus = getMessageUiStatus(state.currentSelectedMessage)
      if (uiStatus !== 'scheduled' && !state.currentSelectedMessage?.meta?.initiator?.powerup) return null
      const countdown = calculateScheduleMessageCountdown(state.currentSelectedMessage, rootState.applicationModule.applicationTime)
      return generateScheduledMessageCountdownTime(countdown)
    },
    selectedCsvPreviewContact(state): CsvPreviewContact | null {
      if (!state.csvPreviewContacts
        || state.selectedCsvPreviewContactIndex === null
        || !state.displayCsvPreviewContacts) return null
      const selectedContact = state.csvPreviewContacts[state.selectedCsvPreviewContactIndex]
      return selectedContact || null
    },
    getCurrentFilterExpression(state): RecipientFilter | null {
      // Returns the most appropriate filter expression. If we have a current message get it from there,
      // otherwise get it from a scratch message.
      if (!state) return null

      if (!!state.currentSelectedMessage
        && !!state.currentSelectedMessage.meta.recipientFilter
        && (!state.emailMessageForm.meta.recipientFilter && !state.emailMessageForm.oid)
        && (!state.simpleMessageForm.meta.recipientFilter && !state.simpleMessageForm.oid)
      ) {
        return clone(state.currentSelectedMessage.meta.recipientFilter)
      }

      if (state.emailMessageForm?.meta?.recipientFilter?.conditions?.length > 0) {
        return clone(state.emailMessageForm.meta.recipientFilter)
      }
      if (state.simpleMessageForm?.meta?.recipientFilter?.conditions?.length > 0) {
        return clone(state.simpleMessageForm.meta.recipientFilter)
      }

      return null
    },
    conversionStats(state) {
      try {
        return state.conversionStatsByTimeWindows[state.conversionTimeWindow]
      }
      catch (e) {
        return []
      }
    },
    conversionGraphTimeseries(state) {
      try {
        return state.conversionGraphTimeseriesByTimeWindows[state.conversionTimeWindow]
      }
      catch (e) {
        return []
      }
    },
  },
}

export default messageModule
